import { z } from 'zod';
/** Caregiver lifecycle status — matches the Prisma CaregiverStatus enum.
 *  Phase 5g (change-list 1.1): ONBOARDING sits between UNDER_REVIEW and
 *  ACTIVE — vetting approved, onboarding (banking + SIN + contract)
 *  incomplete. Order mirrors the Prisma enum. */
export const CaregiverStatusSchema = z.enum([
    'APPLYING',
    'UNDER_REVIEW',
    'ONBOARDING',
    'ACTIVE',
    'SUSPENDED',
    'TERMINATED',
]);
/** Self-identified gender — matches the Prisma `Gender` enum. Mandatory at
 *  application. Generic + inclusive (Ontario Human Rights Code — gender identity
 *  is a protected ground). `PREFER_NOT_TO_SAY` is a valid choice but excludes the
 *  caregiver from same-gender matching (v1.x); the application UI carries that
 *  disclosure. */
export const GenderSchema = z.enum([
    'FEMALE',
    'MALE',
    'NON_BINARY',
    'PREFER_NOT_TO_SAY',
]);
/** POST /api/v1/caregivers/apply body.
 *
 *  Public form on the marketing site. Creates a User (if one doesn't exist
 *  for this email) + a Caregiver row in APPLYING status + a RoleAssignment
 *  with persona=CAREGIVER. The flow continues with a magic-link email
 *  sent to the address — the caregiver clicks, signs in, and uses the
 *  authenticated /caregivers/me endpoints to fill in the rest.
 *
 *  SIN + EFT banking are intentionally NOT collected here — they arrive
 *  post-activation via /caregivers/me/tax-info (PII minimisation, see
 *  Phase 5 scope notes). */
export const CaregiverApplicationRequestSchema = z.object({
    email: z.string().email().max(254).toLowerCase(),
    // Honeypot — humans leave it empty; a filled value is silently dropped
    // server-side (audit medium — mirrors the contact + facility-lead forms).
    _botField: z.string().max(200).optional(),
    /** Cloudflare Turnstile token — human-verification; checked by TurnstileGuard.
     *  Optional so dev/e2e (no site key) still submit; enforced when a key is set. */
    turnstileToken: z.string().max(4096).optional(),
    // Names — legal split per CRA T4A shape (Q11); preferred is what
    // operators + the caregiver see day-to-day.
    legalGivenName: z.string().min(1).max(120).trim(),
    legalSurname: z.string().min(1).max(120).trim(),
    legalMiddleNames: z.string().max(240).trim().nullable().optional(),
    preferredName: z.string().min(1).max(120).trim().nullable().optional(),
    // Date of birth required at apply time so operator can verify 18+
    // before any vetting work begins. ISO YYYY-MM-DD.
    dateOfBirth: z.string().date(),
    // Self-identified gender — MANDATORY. Collected here (like DOB) so it's on
    // file from day one; used later to honour same-gender personal-care requests.
    gender: GenderSchema,
    // Mobile is the primary contact channel for shift coordination + future
    // SMS notifications (v1.x); collected at apply time so the operator can
    // call back if they need clarification on the application.
    phoneMobile: z.string().min(10).max(30).trim(),
    // Phase 17 (change-list 1.2) — location collected up-front so the operator
    // can qualify the lead by region before any vetting work begins. Maps to the
    // existing nullable `Caregiver.city` / `Caregiver.postalCode` columns.
    //
    // Postal code is collected WITH the city because a city on its own is a
    // near-useless location signal: "Toronto" spans ~630 km², so a caregiver in
    // Scarborough and one in Etobicoke are ~30 km apart under the same label.
    // The postal code (even just its FSA, e.g. "M5V") pins a neighbourhood.
    //
    // Deliberately NOT geocoded at apply time: the matching engine only consumes
    // geo for ACTIVE caregivers, and activation already requires the full street
    // address — which fires the real geocode hook (Phase 14e-a). Geocoding here
    // would be redundant work that the precise address immediately overwrites.
    city: z.string().min(1).max(120).trim(),
    // Canadian postal code. Accepts "M5V 2A8" / "m5v2a8" and normalises to the
    // canonical uppercase, space-separated form. Format-validated only — NOT
    // restricted to Ontario FSAs (K/L/M/N/P): someone relocating to Ontario is a
    // legitimate applicant, and the operator qualifies out-of-province leads
    // during vetting rather than the form hard-blocking them.
    postalCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/, 'Enter a valid Canadian postal code (e.g. M5V 2A8)')
        .transform((v) => `${v.replace(/[\s-]/g, '').slice(0, 3)} ${v.replace(/[\s-]/g, '').slice(3)}`),
    // Role codes the applicant declares — strings matching Role.code
    // (PSW / RPN / RN / COMPANION / DSW / COOK per Q7 + Phase 17). Service layer
    // validates against the role reference table.
    declaredRoleCodes: z.array(z.string().min(1).max(40)).min(1).max(6),
    // Optional declared skills — v1 only canonical value is "FOOTCARE"
    // (Q7 — Footcare is a skill modifier on RPN/RN profiles, not a role).
    declaredSkillCodes: z.array(z.string().min(1).max(40)).max(10).optional(),
    // Optional "Referred by" free-text (change-list, 2026-08-11). Who referred
    // the applicant, or how they heard about us — a name, "a friend", "Facebook".
    // Intake-only: recorded on the Caregiver row at apply time, operator-visible
    // on the admin detail, never portal-editable. Purely informational — not
    // validated against any registry.
    referralSource: z.string().max(200).trim().nullable().optional(),
    // Consent acknowledgements — the marketing form gates submission on
    // these checkboxes; recording timestamps gives us audit-trail proof.
    privacyPolicyAcceptedAt: z.string().datetime(),
    contractorAgreementAcceptedAt: z.string().datetime(),
});
/** Always-200 envelope — no enumeration about whether the email already
 *  exists or already has a caregiver application. The follow-up email
 *  content varies based on state ("welcome / continue your application /
 *  application already in progress"), but the response shape is uniform. */
export const CaregiverApplicationResponseSchema = z.object({
    status: z.literal('queued'),
    message: z.string(),
});
/** Phase 5f-3 — GET /api/v1/me/caregiver/application-readiness.
 *
 *  Returns whether the caregiver's application is ready for submission
 *  (APPLYING → UNDER_REVIEW) along with a structured list of what's
 *  missing. Source of truth: `activation-requirements.ts`'s
 *  `checkActivationRequirements` (per 5f-6 — single matrix shared with
 *  the admin activate-guard).
 *
 *  Phase 5f-d audit r1 — `missing[]` is the STRUCTURED shape per the
 *  5f-3 lock (was string codes during the 5f-a → 5f-d ship window).
 *  Each item has `category` (one of PROFILE/CREDENTIAL/COMPLIANCE/
 *  AVAILABILITY) + `type` (the specific subkind) + `reason` (the
 *  human-readable message the portal renders directly — no client-side
 *  label map needed). */
export const CaregiverApplicationReadinessMissingItemSchema = z.object({
    // Phase 5g — ONBOARDING category added for the onboarding-readiness view
    // (banking / SIN / contract legs of the ONBOARDING → ACTIVE gate). The
    // same missing-item shape is reused across application- and
    // onboarding-readiness so the portal renders both with one component.
    category: z.enum(['PROFILE', 'CREDENTIAL', 'COMPLIANCE', 'AVAILABILITY', 'ONBOARDING']),
    /** Specific subkind within the category. Examples:
     *  - PROFILE: 'PHONE_MISSING' | 'ADDRESS_INCOMPLETE'
     *  - CREDENTIAL: 'NACC' | 'CNO_REGISTRATION' | 'VSC' | 'GOVERNMENT_ID'
     *  - COMPLIANCE: 'TB_TEST' | 'VACCINATION' | 'GENERAL_LIABILITY_INSURANCE' | 'WSIB_CLEARANCE'
     *  - AVAILABILITY: 'NONE'
     *  EXPIRED variants surface via a suffix on type (e.g., 'VSC' with
     *  reason mentioning expiry). */
    type: z.string(),
    /** Human-readable label the portal renders verbatim. Backend owns the
     *  copy so future i18n / wording changes ship without portal release. */
    reason: z.string(),
});
export const CaregiverApplicationReadinessResponseSchema = z.object({
    ready: z.boolean(),
    missing: z.array(CaregiverApplicationReadinessMissingItemSchema),
    /** Caregiver's current status — included for portal convenience so
     *  it doesn't need a second round-trip. The Submit-for-review
     *  button only renders when status === 'APPLYING'. */
    status: CaregiverStatusSchema,
});
//# sourceMappingURL=application.schema.js.map